/**
 * Utility functions for calendar operations in ConsultantReportsPage
 */

// Constants for calendar bounds
export const CALENDAR_MIN_YEAR = 2020;
export const CALENDAR_MAX_YEARS_FUTURE = 5;

/**
 * Get the current year for calendar bounds
 */
export function getCurrentYear(): number {
  return new Date().getFullYear();
}

/**
 * Get the maximum allowed year for calendar navigation
 */
export function getMaxYear(): number {
  return getCurrentYear() + CALENDAR_MAX_YEARS_FUTURE;
}

/**
 * Check if a date is within allowed calendar bounds
 */
export function isDateWithinBounds(date: Date): boolean {
  const year = date.getFullYear();
  return year >= CALENDAR_MIN_YEAR && year <= getMaxYear();
}

/**
 * Add months to a date, respecting calendar bounds
 * Returns null if the resulting date would be out of bounds
 */
export function addMonths(date: Date, months: number): Date | null {
  const newDate = new Date(date);
  newDate.setMonth(newDate.getMonth() + months);

  if (!isDateWithinBounds(newDate)) {
    return null;
  }

  return newDate;
}

/**
 * Format a date as YYYY-MM-DD string for consistent date keys
 */
export function formatDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Parse a YYYY-MM-DD string back to a Date object
 */
export function parseDateKey(dateKey: string): Date {
  const [year, month, day] = dateKey.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Get the formatted month and year display string for calendar header
 */
export function getMonthYearDisplay(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

/**
 * Get the number of days in a given month
 */
export function getDaysInMonth(date: Date): number {
  const year = date.getFullYear();
  const month = date.getMonth();
  return new Date(year, month + 1, 0).getDate();
}

/**
 * Get the day of the week (0-6, where 0 is Sunday) for the first day of the month
 */
export function getFirstDayOfMonth(date: Date): number {
  const year = date.getFullYear();
  const month = date.getMonth();
  return new Date(year, month, 1).getDay();
}

/**
 * Generate calendar grid data for a given month
 * Returns an array of objects with day number and whether it's part of the current month
 */
export interface CalendarDay {
  day: number; // 1-31 for actual days, null for empty cells
  isCurrentMonth: boolean;
  dateKey: string | null; // YYYY-MM-DD string or null for empty cells
}

export function generateCalendarGrid(date: Date): CalendarDay[] {
  const year = date.getFullYear();
  const month = date.getMonth();
  const daysInMonth = getDaysInMonth(date);
  const firstDayOfWeek = getFirstDayOfMonth(date);

  const grid: CalendarDay[] = [];

  // Add empty cells for days before the first day of the month
  for (let i = 0; i < firstDayOfWeek; i++) {
    grid.push({
      day: 0, // Use 0 to indicate empty cell
      isCurrentMonth: false,
      dateKey: null
    });
  }

  // Add days of the current month
  for (let day = 1; day <= daysInMonth; day++) {
    const currentDate = new Date(year, month, day);
    grid.push({
      day,
      isCurrentMonth: true,
      dateKey: formatDateKey(currentDate)
    });
  }

  // Optionally fill to complete the last row (for consistent 6-row grid)
  const totalCells = Math.ceil((firstDayOfWeek + daysInMonth) / 7) * 7;
  const remainingCells = totalCells - grid.length;

  for (let i = 0; i < remainingCells; i++) {
    grid.push({
      day: 0,
      isCurrentMonth: false,
      dateKey: null
    });
  }

  return grid;
}

/**
 * Check if navigation in a direction is allowed from the current date
 */
export function canNavigate(direction: 'prev' | 'next', currentDate: Date): boolean {
  const months = direction === 'prev' ? -1 : 1;
  const newDate = addMonths(currentDate, months);
  return newDate !== null;
}
